import { useState } from 'react'
import { getTeamLogoUrl } from '../../lib/teamLogos'
import { formatOdds, calculateRiskPoints, calculateRewardPoints } from '../../lib/scoring'
import { formatPeriod } from '../../lib/formatPeriod'
import { useNbaDfsPlayerLookup } from '../../hooks/useLeagues'
import { getTeamAbbr } from '../../lib/teamLogos'
import PlayerDetailModal from '../ui/PlayerDetailModal'
import PropSliderCard from './PropSliderCard'

const sideStyles = {
  default: 'bg-bg-primary hover:bg-bg-card-hover border-border hover:border-border-hover',
  selected: 'bg-accent/20 border-accent',
  'locked-selected': 'bg-bg-primary border-accent/50 cursor-not-allowed',
  locked: 'bg-bg-primary border-border opacity-60 cursor-not-allowed',
  correct: 'bg-correct-muted border-correct',
  incorrect: 'bg-incorrect-muted border-incorrect',
  // A push is neither. Yellow is already the app's "no result" colour —
  // survivor uses it for survived-but-wrong and for a missed pick.
  push: 'bg-yellow-500/10 border-yellow-500/60',
}

function getSideState(prop, pick, side) {
  if (!pick) {
    if (prop.status !== 'published') return 'locked'
    return 'default'
  }
  if (pick.picked_side !== side) {
    if (pick.status === 'settled') return 'default'
    if (pick.status === 'locked') return 'locked'
    return 'default'
  }
  // This side is picked
  if (pick.status === 'settled') {
    // is_correct is NULL on a push (the player didn't take the field), and
    // null is falsy — so a returned stake was styled identically to a loss.
    if (pick.is_correct == null) return 'push'
    return pick.is_correct ? 'correct' : 'incorrect'
  }
  if (pick.status === 'locked') return 'locked-selected'
  return 'selected'
}

function abbreviateTeam(name) {
  if (!name) return ''
  // Prefer the canonical ESPN abbreviation when we know the team. Falls
  // back to a first-word / initials heuristic only for unrecognized names
  // (e.g. exhibition teams, mock entries).
  const known = getTeamAbbr(name)
  if (known) return known
  const words = name.split(' ')
  if (words.length <= 2) return words[0].slice(0, 3).toUpperCase()
  return words.map((w) => w[0]).join('').toUpperCase()
}


// The odds to SHOW for a side.
//
// A prop is re-priced continuously, but a pick is locked at the odds that
// were live when it was made — odds_at_pick, with risk/reward snapshotted
// alongside. Rendering prop.over_odds for a side the user has already taken
// shows them a number that was never theirs: Ustadbadger picked Pete Alonso
// to homer at +300 and earned 30, then the prop drifted to +510 and his own
// card told him he should have had 51.
//
// So: once a side is picked, that side shows what the user locked. The
// untaken side keeps showing live odds, which is correct — it is still
// available at that price.
function lockedOddsFor(pick, side, liveOdds) {
  if (pick && pick.picked_side === side && pick.odds_at_pick != null) {
    return {
      odds: pick.odds_at_pick,
      risk: pick.risk_at_submission ?? pick.risk_points ?? calculateRiskPoints(pick.odds_at_pick),
      reward: pick.reward_at_submission ?? pick.reward_points ?? calculateRewardPoints(pick.odds_at_pick),
    }
  }
  return { odds: liveOdds, risk: calculateRiskPoints(liveOdds), reward: calculateRewardPoints(liveOdds) }
}

export default function PropCard({ prop, picks, pick: pickProp, onPick, onUndoPick, isSubmitting, compact }) {
  // A prop can carry several bets now. The over/under card below only ever
  // shows one, so it takes the first; the slider card takes the whole list.
  // `pick` is still accepted for the callers that pass a single one.
  const pick = pickProp ?? (picks || [])[0] ?? null
  const isLocked = prop.status !== 'published'
  const isSettled = prop.status === 'settled'
  const [showPlayerModal, setShowPlayerModal] = useState(false)
  const sportKey = prop.games?.sports?.key || prop.sport_key || 'basketball_nba'
  // College football is the one sport where we have no headshots at all —
  // 0 of 1,322 distinct prop players. ESPN publishes them, but NFL headshots
  // come from our own nfl_players table and there is no NCAAF equivalent, so
  // nothing fills the field. Rather than show every college card with an
  // empty disc (or initials that are just the name again, two inches left of
  // the name), the avatar is dropped entirely for NCAAF and the text slides
  // over. Nick's call 2026-10-17; revisit if we ever source college
  // headshots. NCAAB is in the same state but carries almost no prop volume.
  const isNcaaf = sportKey === 'americanfootball_ncaaf'

  // Markets the server could calibrate a line ladder for render as a slider
  // instead of over/under. Everything else — anytime TD, every non-football
  // sport, and any market whose hit rate we haven't measured — keeps this
  // card unchanged. `compact` opts out too: the slider needs vertical room.
  if (prop.slider?.stops?.length > 1 && !compact) {
    return (
      <PropSliderCard
        prop={prop}
        picks={picks || (pick ? [pick] : [])}
        onPickLine={(propId, line, side) => onPick?.(propId, side || 'over', line)}
        onUndoPick={onUndoPick}
        isSubmitting={isSubmitting}
        sportKey={sportKey}
      />
    )
  }

  // Anytime touchdown is a Yes/No market stored at line 0.5 (see
  // propService.normalizeOutcome). "Will X go over or under 0.5 Anytime TD?"
  // is a clumsy way to ask a question the sportsbook asks plainly, and the
  // buttons are Yes/No rather than Over/Under — so the card says so.
  //
  // Books commonly price only the Yes side, which is fine: the No button
  // already renders conditionally on under_odds.
  const isYesNo = prop.market_key === 'player_anytime_td'
  // Team defenses ("Cleveland Browns D/ST") have no player row and never
  // will, so their headshot isn't a button — better an honest non-target than
  // a tap that silently does nothing, which is how a missing player lookup
  // fails here.
  const isTeamDefense = / D\/ST$/.test(prop.player_name || '')
  // "Cleveland Browns D/ST" -> the Browns crest. The resolver keys on the
  // franchise name, so the suffix has to come off first or it returns null.
  const teamLogoUrl = isTeamDefense
    ? getTeamLogoUrl(String(prop.player_name).replace(/ D\/ST$/, ''), sportKey)
    : null

  // "Thu 9/3" when the game is on a later day, otherwise null.
  const kickoffLabel = (() => {
    const iso = prop.games?.starts_at
    if (!iso) return null
    const PT = { timeZone: 'America/Los_Angeles' }
    const day = new Date(iso).toLocaleDateString('en-CA', PT)
    const today = new Date().toLocaleDateString('en-CA', PT)
    if (day === today) return null
    return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', ...PT })
  })()
  // Which score column is authoritative depends on the game's STATUS, and
  // preferring live_* blanket is wrong. live_* stops updating at the last
  // poll before a game ends, so it is stale on finals across every sport —
  // 108 of 560 NBA finals disagree with the real result, 211 of 1461 NCAAB,
  // 66 of 684 NHL. A blanket `live ?? final` would have shown the Grizzlies
  // beating the Kings 123-110 when they won 123-112.
  //
  // So: live games read live_*, finished games read the final columns, and
  // each falls back to the other only when its own is missing. ?? not || —
  // a genuine 0 is a score, not an absence.
  const isFinalGame = prop.games?.status === 'final'
  const awayScore = isFinalGame
    ? (prop.games?.away_score ?? prop.games?.live_away_score)
    : (prop.games?.live_away_score ?? prop.games?.away_score)
  const homeScore = isFinalGame
    ? (prop.games?.home_score ?? prop.games?.live_home_score)
    : (prop.games?.live_home_score ?? prop.games?.home_score)

  const isNba = sportKey.includes('basketball_nba')
  const { data: playerLookup } = useNbaDfsPlayerLookup(showPlayerModal ? prop.player_name : null, sportKey)

  function handleClick(side) {
    if (isLocked || isSubmitting) return
    if (pick?.picked_side === side && pick?.status === 'pending') {
      onUndoPick?.(prop.id)
    } else {
      onPick?.(prop.id, side)
    }
  }

  const overState = getSideState(prop, pick, 'over')
  const underState = getSideState(prop, pick, 'under')

  return (
    <div className={`bg-bg-primary/50 backdrop-blur-sm rounded-2xl border ${pick?.status === 'locked' ? 'border-accent' : 'border-text-primary/20'} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex-1 min-w-0">
          <span className="font-semibold text-sm text-text-primary">
            {isYesNo
              ? `Will ${prop.player_name} score a touchdown?`
              : pick?.chosen_line != null
              // A slider pick rendered through this card (locked, or in a
              // compact list) asks the question the user actually answered.
              ? `Will ${prop.player_name} go over ${pick.chosen_line} ${prop.market_label}?`
              : `Will ${prop.player_name} go over or under ${prop.line} ${prop.market_label}?`}
          </span>
        </div>
        {prop.games && (
          <div className="text-right whitespace-nowrap ml-2">
            <div className="text-xs text-text-muted">
              {abbreviateTeam(prop.games.away_team)} @ {abbreviateTeam(prop.games.home_team)}
            </div>
            {/* Football props publish days ahead, so the matchup alone leaves
                you guessing when it is. Shown only when the game ISN'T today —
                on a same-day slate the date is noise. PT-anchored to match
                the rest of the app. */}
            {kickoffLabel && (
              <div className="text-[10px] text-text-muted">{kickoffLabel}</div>
            )}
            {/* Score sits on its own line under the matchup rather than
                inline with it: the block is whitespace-nowrap and already
                right-aligned against the prop body, so widening the matchup
                line pushes the whole card. live_* is preferred over the
                final columns because an in-progress game only updates the
                live pair — but both are read, so a game that has gone final
                still shows a score here rather than blanking. */}
            {prop.games.status === 'live' && (
              <div className="text-[10px] text-accent">
                {awayScore != null && homeScore != null && (
                  <span className="font-semibold">{awayScore}-{homeScore} </span>
                )}
                {prop.games.period && (
                  <>{formatPeriod(sportKey, prop.games.period)} {prop.games.clock}</>
                )}
              </div>
            )}
            {prop.games.status === 'final' && (
              <div className="text-[10px] text-text-muted font-semibold">
                {awayScore != null && homeScore != null ? `${awayScore}-${homeScore} Final` : 'Final'}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {teamLogoUrl ? (
          // A team defense has no headshot, and the initials fallback gave
          // "CBD" — meaningless. The crest is what identifies the unit being
          // picked, and it's the same artwork the scores strip uses.
          <div className="shrink-0 w-20 h-20 rounded-full bg-bg-secondary flex items-center justify-center">
            <img
              src={teamLogoUrl}
              alt={prop.player_name}
              className="w-14 h-14 object-contain"
              onError={(e) => {
                const std = teamLogoUrl.replace('/500-dark/', '/500/')
                if (e.currentTarget.src !== std) e.currentTarget.src = std
                else e.currentTarget.style.visibility = 'hidden'
              }}
            />
          </div>
        ) : isNcaaf ? null : prop.player_headshot_url ? (
          <button
            onClick={() => setShowPlayerModal(true)}
            disabled={isTeamDefense}
            className={`shrink-0 ${isTeamDefense ? '' : 'cursor-pointer'}`}
          >
            <img
              src={prop.player_headshot_url}
              alt={prop.player_name}
              className="w-20 h-20 rounded-full object-cover bg-bg-secondary"
            />
          </button>
        ) : (
          // Fallback initials when the headshot lookup whiffed (recent
          // call-up not yet in the ESPN roster cache or the DFS salary
          // table). Blank circle looks broken; initials at least anchor
          // the card visually.
          <div className="shrink-0 w-20 h-20 rounded-full bg-bg-secondary flex items-center justify-center text-text-muted font-bold text-lg">
            {(prop.player_name || '')
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((n) => n[0])
              .join('')
              .toUpperCase()}
          </div>
        )}
        <div className="flex gap-2 flex-1">
          <button
            onClick={() => handleClick('over')}
            disabled={isLocked || isSubmitting || overState === 'locked' || overState === 'correct' || overState === 'incorrect' || overState === 'push'}
            className={`flex-1 p-2.5 rounded-xl border transition-all ${sideStyles[overState]} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className={`font-semibold text-xs mb-0.5 ${overState === 'correct' ? 'text-correct' : overState === 'incorrect' ? 'text-incorrect' : overState === 'push' ? 'text-yellow-500' : 'text-text-primary'}`}>
              {isYesNo ? 'Yes' : 'Over'}
            </div>
            {prop.over_odds && (
              <div className="text-center">
                <div className={`font-semibold text-xs ${overState === 'selected' ? 'text-white' : ''}`}>
                  <span className="text-incorrect">-{lockedOddsFor(pick, 'over', prop.over_odds).risk}</span>
                  <span className={overState === 'selected' ? 'text-white/70' : 'text-text-muted'}> → </span>
                  <span className="text-correct">+{lockedOddsFor(pick, 'over', prop.over_odds).reward}</span>
                </div>
                <div className={`text-xs ${overState === 'selected' ? 'text-white/70' : 'text-text-muted'}`}>
                  {formatOdds(lockedOddsFor(pick, 'over', prop.over_odds).odds)}
                </div>
              </div>
            )}
          </button>

          {prop.under_odds && (
            <button
              onClick={() => handleClick('under')}
              disabled={isLocked || isSubmitting || underState === 'locked' || underState === 'correct' || underState === 'incorrect' || underState === 'push'}
              className={`flex-1 p-2.5 rounded-xl border transition-all ${sideStyles[underState]} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className={`font-semibold text-xs mb-0.5 ${underState === 'correct' ? 'text-correct' : underState === 'incorrect' ? 'text-incorrect' : underState === 'push' ? 'text-yellow-500' : 'text-text-primary'}`}>
                {isYesNo ? 'No' : 'Under'}
              </div>
              <div className="text-center">
                <div className={`font-semibold text-xs ${underState === 'selected' ? 'text-white' : ''}`}>
                  <span className="text-incorrect">-{lockedOddsFor(pick, 'under', prop.under_odds).risk}</span>
                  <span className={underState === 'selected' ? 'text-white/70' : 'text-text-muted'}> → </span>
                  <span className="text-correct">+{lockedOddsFor(pick, 'under', prop.under_odds).reward}</span>
                </div>
                <div className={`text-xs ${underState === 'selected' ? 'text-white/70' : 'text-text-muted'}`}>
                  {formatOdds(lockedOddsFor(pick, 'under', prop.under_odds).odds)}
                </div>
              </div>
            </button>
          )}
        </div>
      </div>

      {pick?.status === 'settled' && pick.is_correct == null ? (
        // "0 pts" alone read as a loss. Name the outcome and the reason —
        // the stake came back, nothing was lost.
        //
        // TWO different pushes, and the copy claimed the wrong one. A null
        // actual_value means the player never took the field; an actual
        // equal to the line means he played and landed exactly on it. Sam
        // Darnold threw for 195 against a 195 line and the card said he
        // didn't play.
        <div className="mt-3 text-center text-sm font-semibold text-yellow-500">
          {prop.actual_value != null
            ? `Push — landed exactly on ${pick.chosen_line ?? prop.line} · stake returned`
            : `Push — ${prop.player_name} didn't play · stake returned`}
        </div>
      ) : pick?.status === 'settled' && pick.points_earned !== null && (
        <div className={`mt-3 text-center text-sm font-semibold ${pick.points_earned > 0 ? 'text-correct' : pick.points_earned < 0 ? 'text-incorrect' : 'text-text-muted'}`}>
          {pick.points_earned > 0 ? '+' : ''}{pick.points_earned} pts
        </div>
      )}

      {pick?.status === 'locked' && pick.odds_at_pick != null && (
        <div className="mt-3 text-center text-sm text-text-muted">
          <span className="text-incorrect">-{pick.risk_at_submission || calculateRiskPoints(pick.odds_at_pick)}</span>
          {' / '}
          <span className="text-correct">+{pick.reward_at_submission || calculateRewardPoints(pick.odds_at_pick)}</span>
          {pick.multiplier > 1 && (
            <span className="ml-1.5 text-[10px] font-bold text-accent px-1.5 py-0.5 rounded">
              {pick.multiplier}x
            </span>
          )}
        </div>
      )}

      {pick?.live_stat != null && pick?.status === 'locked' && (
        <div className="mt-2 text-center">
          <span className="font-display text-lg text-white">{pick.live_stat}</span>
          <span className="text-xs text-text-muted ml-1.5">{prop.market_label}</span>
        </div>
      )}
      {(prop?.actual_value != null || pick?.actual_value != null || (pick?.live_stat != null && pick?.status === 'settled')) && pick?.status === 'settled' && (
        <div className="mt-3 pt-3 border-t border-text-primary/10 text-center">
          <span className="text-xs text-text-muted mr-1.5">Actual:</span>
          <span className="font-display text-xl text-white">{prop.actual_value ?? pick.actual_value ?? pick.live_stat}</span>
          <span className="text-xs text-text-muted ml-1.5">{prop.market_label}</span>
        </div>
      )}

      {showPlayerModal && playerLookup && (
        <PlayerDetailModal
          player={playerLookup}
          onClose={() => setShowPlayerModal(false)}
          sport={prop.games?.sports?.key || 'basketball_nba'}
        />
      )}
    </div>
  )
}
